/**
 * ErrorBoundary - Componente para capturar errores de React
 * Muestra un mensaje amigable cuando ocurre un error
 */
import { Component } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Typography,
  Paper,
  Container,
} from '@mui/material';
import { ErrorOutline, Refresh } from '@mui/icons-material';

/**
 * @component ErrorBoundary
 * @description Captura errores en componentes hijos y muestra UI de error
 */
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error) {
    // Actualizar estado para mostrar UI de fallback
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Registrar el error
    console.error('Error capturado por ErrorBoundary:', error, errorInfo);

    this.setState({
      error,
      errorInfo,
    });

    // Aquí podrías enviar el error a un servicio de logging
    // logErrorToService(error, errorInfo);
  }

  handleReload = () => {
    // Recargar la página
    window.location.reload();
  };

  handleReset = () => {
    // Resetear el error y volver a intentar renderizar
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  render() {
    if (this.state.hasError) {
      // UI de error
      return (
        <Container maxWidth="md">
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '100vh',
              py: 4,
            }}
          >
            <Paper
              elevation={3}
              sx={{
                p: 4,
                textAlign: 'center',
                maxWidth: 500,
                width: '100%',
              }}
            >
              {/* Icono de error */}
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  bgcolor: 'error.light',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 3,
                }}
              >
                <ErrorOutline
                  sx={{
                    fontSize: 48,
                    color: 'error.main',
                  }}
                />
              </Box>

              {/* Título */}
              <Typography
                variant="h5"
                color="text.primary"
                sx={{ fontWeight: 600, mb: 2 }}
              >
                Algo salió mal
              </Typography>

              {/* Mensaje */}
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{ mb: 3 }}
              >
                Lo sentimos, ha ocurrido un error inesperado. Por favor, intenta
                recargar la página.
              </Typography>

              {/* Detalles del error en desarrollo */}
              {process.env.NODE_ENV === 'development' && this.state.error && (
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    mb: 3,
                    textAlign: 'left',
                    bgcolor: 'grey.50',
                    maxHeight: 200,
                    overflow: 'auto',
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontFamily: 'monospace',
                      fontSize: '0.75rem',
                      color: 'error.main',
                      mb: 1,
                    }}
                  >
                    {this.state.error.toString()}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontFamily: 'monospace',
                      fontSize: '0.7rem',
                      color: 'text.secondary',
                      whiteSpace: 'pre-wrap',
                    }}
                  >
                    {this.state.errorInfo?.componentStack}
                  </Typography>
                </Paper>
              )}

              {/* Botones de acción */}
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                <Button
                  variant="outlined"
                  color="inherit"
                  onClick={this.handleReset}
                >
                  Intentar de nuevo
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<Refresh />}
                  onClick={this.handleReload}
                >
                  Recargar página
                </Button>
              </Box>
            </Paper>
          </Box>
        </Container>
      );
    }

    // Renderizar componentes hijos normalmente
    return this.props.children;
  }
}

ErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired,
};

export default ErrorBoundary;
